<template>
  <div id="calendar" ref="calendarRef">
    <FullCalendar ref="fullCalendar" :key="calendarKey" :options="calendarOptions" />
  </div>
</template>

<script>
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import timeGridPlugin from "@fullcalendar/timegrid";
import FullCalendar from "@fullcalendar/vue3";
import { computed, onMounted, ref } from "vue";
import { useAuthStore } from "../../stores/authStore";
import { format } from "date-fns";

export default {
  name: "CalendarDisplay",

  components: {
    FullCalendar,
  },

  props: {
    events: {
      type: Array,
      default: () => [],
    },
    holidayHours: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: [
    "dateRangeChange",
    "eventClick",
    "dateSelect",
    "eventContextMenu",
    "dayContextMenu",
    "eventChange",
  ],

  setup(props, { emit }) {
    const calendarRef = ref(null);
    const fullCalendar = ref(null);
    const authStore = useAuthStore();
    const isAdmin = computed(() => authStore.isAdmin);

    // Static key - events and holidayHours are reactive props, no need to re-mount
    const calendarKey = 'calendar';

    // Handle date range change in calendar
    const handleDateRangeChange = (dateInfo) => {
      emit("dateRangeChange", dateInfo);
    };

    // Handle clicking on an event
    const handleEventClick = (info) => {
      emit("eventClick", info.event);
    };

    // Handle selecting a date range
    const handleDateSelect = (selectInfo) => {
      emit("dateSelect", selectInfo);
    };

    // Handle event changes (drag & drop, resize)
    const handleEventChange = (info) => {
      emit("eventChange", info.event);
    };

    // Handle right-click on event
    const handleEventContextMenu = (e, event) => {
      e.preventDefault();
      e.stopPropagation();
      emit("eventContextMenu", e, event);
    };

    // Handle right-click on day
    const handleDayContextMenu = (e, date) => {
      e.preventDefault();
      e.stopPropagation();
      emit("dayContextMenu", e, date);
    };

    // Calendar options
    const calendarOptions = computed(() => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin],
      initialView: "dayGridMonth",
      timeZone: 'America/New_York',
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek",
      },
      editable: isAdmin.value, // Only editable for admins
      selectable: isAdmin.value, // Only selectable for admins
      selectMirror: isAdmin.value, // Only show selection mirror for admins
      dayMaxEvents: 5, // Show max 5 shifts per cell
      events: props.events,
      eventClick: handleEventClick,
      select: isAdmin.value ? handleDateSelect : null, // Only handle date selection for admins
      datesSet: handleDateRangeChange,
      eventChange: isAdmin.value ? handleEventChange : null, // Only handle event changes for admins
      eventDidMount: (info) => {
        // Only add context menu for admins
        if (isAdmin.value) {
          info.el.addEventListener("contextmenu", (e) => {
            handleEventContextMenu(e, info.event);
          });
        }
      },
      dayCellDidMount: (info) => {
        // Only add context menu for admins
        if (isAdmin.value) {
          info.el.addEventListener("contextmenu", (e) => {
            handleDayContextMenu(e, { date: info.date });
          });
        }

        // Add holiday badge if applicable
        const dateStr = format(info.date, 'yyyy-MM-dd');
        const dayHours = props.holidayHours[dateStr];

        if (dayHours && (dayHours.is_closed || dayHours.is_holiday)) {
          const dayTop = info.el.querySelector('.fc-daygrid-day-top');
          if (dayTop) {
            const badge = document.createElement('span');

            if (dayHours.is_closed) {
              badge.className = 'holiday-badge closed';
              badge.textContent = 'CLOSED';
            } else if (dayHours.open && dayHours.close) {
              badge.className = 'holiday-badge modified';
              // Format time as HH:MM (remove seconds)
              const openTime = dayHours.open.slice(0, 5);
              const closeTime = dayHours.close.slice(0, 5);
              badge.textContent = `${openTime}-${closeTime}`;
            }

            if (dayHours.note) {
              badge.title = dayHours.note;
            }

            // Style the container for flex layout
            dayTop.style.display = 'flex';
            dayTop.style.justifyContent = 'space-between';
            dayTop.style.alignItems = 'center';
            dayTop.style.width = '100%';
            dayTop.style.paddingRight = '4px';

            dayTop.appendChild(badge);
          }
        }
      },
      displayEventTime: false,
    }));

    // Methods to expose
    const getCalendarApi = () => {
      return fullCalendar.value?.getApi();
    };

    // Make calendar element accessible for printing
    const getCalendarElement = () => {
      return calendarRef.value;
    };

    // Expose calendar methods and elements
    onMounted(() => {
      // You could emit a 'ready' event here if needed
    });

    return {
      calendarRef,
      fullCalendar,
      calendarOptions,
      calendarKey,
      getCalendarApi,
      getCalendarElement,
    };
  },
};
</script>

<style scoped>
/* Holiday badge styles */
:deep(.holiday-badge) {
  font-size: 8px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  text-transform: uppercase;
}

:deep(.holiday-badge.closed) {
  background-color: #ef4444;
  color: white;
}

:deep(.holiday-badge.modified) {
  background-color: #f97316;
  color: white;
}

/* Print styles for badges */
@media print {
  :deep(.holiday-badge) {
    font-size: 6px;
    padding: 1px 2px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
